import { Material } from "@babylonjs/core/Materials/material";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Scene } from "@babylonjs/core/scene";
import type { TextureGenerator } from "../assets/TextureGenerator";
import { type ProceduralTextureKind } from "../assets/TextureGenerator";

export const MAX_DUNGEON_SIMULTANEOUS_LIGHTS = 8;

export class MaterialFactory {
  private readonly materials = new Map<string, StandardMaterial>();

  public constructor(
    private readonly scene: Scene,
    private readonly textures: TextureGenerator,
  ) {}

  public get(kind: ProceduralTextureKind, emissive = false): StandardMaterial {
    return this.getTiled(kind, 1, 1, emissive);
  }

  public getFloor(kind: ProceduralTextureKind, emissive = false): StandardMaterial {
    return this.getTiledMaterial(kind, 1, 1, emissive, true);
  }

  public getTiled(
    kind: ProceduralTextureKind,
    uScale: number,
    vScale: number,
    emissive = false,
  ): StandardMaterial {
    return this.getTiledMaterial(kind, uScale, vScale, emissive, false);
  }

  private getTiledMaterial(
    kind: ProceduralTextureKind,
    uScale: number,
    vScale: number,
    emissive: boolean,
    matte: boolean,
  ): StandardMaterial {
    const normalizedU = Math.round(uScale * 1000) / 1000;
    const normalizedV = Math.round(vScale * 1000) / 1000;
    const key = `${kind}:${emissive ? "emissive" : "lit"}:${matte ? "matte" : "standard"}:${normalizedU}:${normalizedV}`;
    const cached = this.materials.get(key);
    if (cached) {
      return cached;
    }
    const material = new StandardMaterial(key, this.scene);
    material.maxSimultaneousLights = MAX_DUNGEON_SIMULTANEOUS_LIGHTS;
    const texture = this.createTexture(kind);
    texture.uScale = normalizedU;
    texture.vScale = normalizedV;
    material.diffuseTexture = texture;
    material.specularColor = matte ? Color3.Black() : new Color3(0.05, 0.06, 0.05);
    material.emissiveColor = new Color3(0.045, 0.052, 0.045);
    material.roughness = 0.88;
    if (emissive) {
      material.emissiveTexture = texture;
      material.emissiveColor =
        kind === "toxicFloor" || kind === "greenEnergyChannel"
          ? new Color3(0.2, 0.62, 0.14)
          : kind === "amberTerminal"
            ? new Color3(0.72, 0.31, 0.06)
            : new Color3(0.25, 0.48, 0.42);
    }
    if (
      ["bloodDecal", "bloodSmear", "ashDecal", "muzzleFlash", "projectile", "particle"].includes(
        kind,
      )
    ) {
      texture.hasAlpha = true;
      material.useAlphaFromDiffuseTexture = true;
      material.transparencyMode = Material.MATERIAL_ALPHATESTANDBLEND;
      material.disableLighting = !["bloodDecal", "bloodSmear", "ashDecal"].includes(kind);
    }
    this.materials.set(key, material);
    return material;
  }

  public createAtlasMaterial(name: string, canvas: HTMLCanvasElement): StandardMaterial {
    const material = new StandardMaterial(name, this.scene);
    material.maxSimultaneousLights = MAX_DUNGEON_SIMULTANEOUS_LIGHTS;
    const texture = new Texture(
      canvas.toDataURL("image/png"),
      this.scene,
      true,
      true,
      Texture.NEAREST_SAMPLINGMODE,
    );
    texture.hasAlpha = true;
    texture.wrapU = Texture.CLAMP_ADDRESSMODE;
    texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    material.diffuseTexture = texture;
    material.opacityTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.transparencyMode = Material.MATERIAL_ALPHATEST;
    material.alphaCutOff = 0.12;
    material.specularColor = Color3.Black();
    material.emissiveColor = new Color3(0.1, 0.09, 0.08);
    material.backFaceCulling = false;
    return material;
  }

  public dispose(): void {
    for (const material of this.materials.values()) {
      material.dispose(true, true);
    }
    this.materials.clear();
  }

  private createTexture(kind: ProceduralTextureKind): Texture {
    const texture = new Texture(
      this.textures.get(kind).toDataURL("image/png"),
      this.scene,
      false,
      false,
      Texture.NEAREST_SAMPLINGMODE,
    );
    texture.wrapU = Texture.WRAP_ADDRESSMODE;
    texture.wrapV = Texture.WRAP_ADDRESSMODE;
    texture.anisotropicFilteringLevel = 1;
    return texture;
  }
}
